/**
 * Layout Parser Service
 *
 * Promise-based wrapper around the layoutParser WebWorker.
 * Provides progress callbacks and singleton worker management.
 */

import type {
  LayoutTextEntity,
  ClassificationPattern,
  LayoutWorkerResponse,
} from '@/domain/layout/types';

// ── Types ─────────────────────────────────────────────────

export interface LayoutParseOptions {
  onProgress?: (percent: number, message: string) => void;
}

export interface LayoutParseResult {
  entities: LayoutTextEntity[];
  pageWidthPt: number;
  pageHeightPt: number;
  warnings: string[];
}

// ── Worker Singleton ──────────────────────────────────────

let worker: Worker | null = null;
let requestCounter = 0;

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(
      new URL('../workers/layoutParser.worker.ts', import.meta.url),
      { type: 'module' },
    );
  }
  return worker;
}

/**
 * Terminate the layout parser worker (e.g., on page unmount).
 */
export function terminateLayoutParser(): void {
  if (worker) {
    worker.terminate();
    worker = null;
  }
}

// ── Main API ──────────────────────────────────────────────

/**
 * Parse a PDF ArrayBuffer to extract classified text entities.
 * Runs in a WebWorker for UI responsiveness.
 *
 * @param arrayBuffer - PDF file data
 * @param pageIndex - 0-indexed page to extract from
 * @param customPatterns - Optional user-defined classification patterns
 * @param options - Progress callback
 * @returns Parsed entities, page dimensions, and warnings
 */
export function parseLayoutPdf(
  arrayBuffer: ArrayBuffer,
  pageIndex: number,
  customPatterns?: ClassificationPattern[],
  options?: LayoutParseOptions,
): Promise<LayoutParseResult> {
  return new Promise((resolve, reject) => {
    const w = getWorker();
    const requestId = `layout_${++requestCounter}_${Date.now()}`;

    function handleMessage(event: MessageEvent<LayoutWorkerResponse>) {
      const msg = event.data;
      if (!('requestId' in msg) || msg.requestId !== requestId) return;

      if (msg.type === 'progress') {
        options?.onProgress?.(msg.percent, msg.message);
        return;
      }

      // Terminal messages — clean up listener
      w.removeEventListener('message', handleMessage);

      if (msg.type === 'error') {
        reject(new Error(msg.message));
        return;
      }

      if (msg.type === 'result') {
        resolve({
          entities: msg.entities as LayoutTextEntity[],
          pageWidthPt: msg.pageWidthPt,
          pageHeightPt: msg.pageHeightPt,
          warnings: msg.warnings,
        });
        return;
      }

      reject(new Error('Unexpected worker response'));
    }

    w.addEventListener('message', handleMessage);

    // Transfer the ArrayBuffer for zero-copy
    w.postMessage(
      {
        type: 'parse',
        requestId,
        arrayBuffer,
        pageIndex,
        customPatterns,
      },
      [arrayBuffer],
    );
  });
}
